import React, { useEffect, useState, useRef, useCallback } from "react";
import styled from "styled-components";
import { toSvg, toPng, toCanvas } from "html-to-image";
import { palette } from "../../../../assets/styles/Palette";
import { Body3 } from "../../../../assets/styles/Typography";
import { Button } from "../../../../assets/styles/ButtonStyle";
import {
  AUTODISPLAY_DISPLAY_WIDTH,
  AUTODISPLAY_DISPLAY_HEIGHT,
} from "../../../AtomStates";
import { useAtom } from "jotai";
import { useNavigate } from "react-router-dom";
import MoleculeSizeArea from "../molecules/MoleculeSizeArea";

const OrganismTemplatePreview = ({
  template,
  onClose,
  title,
  htmlContent,
  displayWidth,
  displayHeight,
}) => {
  // console.log("🚀 ~ OrganismTemplatePreview ~ htmlContent:", htmlContent);
  const [contentScale, setContentScale] = useState(1);
  const iframeRef = useRef(null);
  const [contentSize, setContentSize] = useState({
    width: parseInt(displayWidth),
    height: parseInt(displayHeight),
  });

  const handleDownloadPng = useCallback(() => {
    if (!iframeRef.current) {
      console.error("Iframe is not available");
      return;
    }

    const iframeBody = iframeRef.current.contentWindow.document.body;

    toPng(iframeBody, {
      cacheBust: true,
      width: contentSize.width,
      height: contentSize.height,
      skipFonts: true,
    })
      .then((dataUrl) => {
        const link = document.createElement("a");
        link.download = `${title || "template"}.png`;
        link.href = dataUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch((err) => {
        console.error("Oops, something went wrong!", err);
      });
  }, [iframeRef, title, contentSize]);

  const handleDownloadSvg = useCallback(() => {
    if (!iframeRef.current) {
      console.error("Iframe is not available");
      return;
    }

    const iframeBody = iframeRef.current.contentWindow.document.body;

    // Canvas로 변환 후 SVG에 이미지로 임베드
    toCanvas(iframeBody, {
      cacheBust: true,
      width: contentSize.width,
      height: contentSize.height,
      skipFonts: true,
    })
      .then((canvas) => {
        const dataUrl = canvas.toDataURL("image/png");

        // 호환 가능한 SVG 생성 (foreignObject 없이)
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${contentSize.width}" height="${contentSize.height}" viewBox="0 0 ${contentSize.width} ${contentSize.height}">
          <image x="0" y="0" width="${contentSize.width}" height="${contentSize.height}" xlink:href="${dataUrl}"/>
        </svg>`;

        const blob = new Blob([svg], { type: "image/svg+xml" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.download = `${title || "template"}_compatible.svg`;
        link.href = url;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // 메모리 정리
        URL.revokeObjectURL(url);
      })
      .catch((err) => {
        console.error("Compatible SVG generation failed!", err);
      });
  }, [iframeRef, title, contentSize]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      onClose();
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [onClose]);

  // 화면 크기에 맞는 스케일 계산
  const calculateScale = () => {
    // 사용 가능한 화면 공간 계산 (헤더와 패딩 제외)
    const headerHeight = 60;
    const padding = 40;
    const availableWidth = window.innerWidth - padding;
    const availableHeight = window.innerHeight - headerHeight - padding;

    // 콘텐츠가 화면에 맞게 스케일링
    const scaleX = availableWidth / contentSize.width;
    const scaleY = availableHeight / contentSize.height;

    return Math.min(scaleX, scaleY, 1); // 최대 1배까지만
  };

  // HTML 콘텐츠가 변경될 때 크기 추출
  useEffect(() => {
    if (htmlContent) {
      // const size = extractContentSize(previewHtmlContent);
      setContentSize({
        width: parseInt(displayWidth),
        height: parseInt(displayHeight),
      });
    }
  }, [htmlContent, displayWidth, displayHeight]);

  // 스케일 계산

  useEffect(() => {
    const updateScale = () => {
      setContentScale(calculateScale());
    };

    updateScale();
    window.addEventListener("resize", updateScale);
    return () => window.removeEventListener("resize", updateScale);
  }, [contentSize]);

  // iframe 로드 후 높이 조정
  useEffect(() => {
    const iframe = iframeRef.current;
    if (iframe) {
      iframe.onload = () => {
        try {
          const iframeDocument =
            iframe.contentDocument || iframe.contentWindow.document;
          if (iframeDocument) {
            iframe.style.height = iframeDocument.body.scrollHeight + "px";
          }
        } catch (e) {
          // cross-origin 이슈시 무시
        }
      };
    }
  }, [htmlContent]);

  const handleGoBack = () => {
    onClose();
  };

  return (
    <PreviewContainer>
      <Header>
        <TitleContainer>
          <TemplateCategory>
            <Body3 color="primary" style={{ fontSize: "12px" }}>
              {template?.category || "디자인 컨셉"}
            </Body3>
          </TemplateCategory>
          <HeaderTitle>{title || "미리보기"}</HeaderTitle>
        </TitleContainer>
        <ButtonGroup>
          <Button Large onClick={handleDownloadPng}>
            PNG로 저장
          </Button>
          <Button Large onClick={handleDownloadSvg}>
            SVG로 저장
          </Button>
          <Button Primary Large Fill onClick={handleGoBack}>
            미리보기 종료하기
          </Button>
        </ButtonGroup>
      </Header>

      {/* 사이즈 영역 */}
      <StyledSizeAreaContainer>
        <MoleculeSizeArea
          title="내 디스플레이"
          width={displayWidth.toString()}
          height={displayHeight.toString()}
        />
      </StyledSizeAreaContainer>

      <PreviewContent>
        {htmlContent ? (
          <OriginalHtmlFrame
            ref={iframeRef}
            srcDoc={htmlContent}
            title="HTML Preview"
            style={{
              width: contentSize.width,
              height: contentSize.height,
              display: "block",
              margin: "0 auto",
            }}
          />
        ) : (
          <div>콘텐츠를 불러오는 중...</div>
        )}
      </PreviewContent>
    </PreviewContainer>
  );
};

export default OrganismTemplatePreview;

const PreviewContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  background: #ebebeb; /* 이미지와 같이 연한 회색 배경을 사용합니다. */

  z-index: 1000;
  display: flex;
  flex-direction: column;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const Header = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 7px 14px;
  background: white;
  border-bottom: 1px solid #e0e0e0;
  flex-shrink: 0; // 헤더는 크기가 줄어들지 않도록 설정
`;

const TitleContainer = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${palette.gray800};
`;

const PreviewContent = styled.div`
  flex-grow: 1;
  overflow: auto; /* 스크롤 활성화 */
  padding: 0;

  background: #ebebeb;

  /* 스크롤바 스타일링 */
  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const TemplateCategory = styled.div`
  color: ${palette.primary};
  font-weight: 500;
  background-color: ${palette.primaryLightest};
  padding: 3px 8px;
  border-radius: 2px;
`;

// 원본 크기 그대로 표시하는 iframe (최소 크기 보장)
const OriginalHtmlFrame = styled.iframe`
  border: none;
  background: #ebebeb;
  display: block;
  height: auto;
`;

// const OriginalHtmlFrame = styled.iframe`
//   width: 100%;
//   min-width: 1200px;
//   min-height: 800px;
//   border: none;
//   background: #ebebeb;
//   display: block;
//   height: auto;
// `;

// 사이즈 영역 컨테이너
const StyledSizeAreaContainer = styled.div`
  top: 56px;
  left: 0;
  right: 0;
  z-index: 99;
`;
